import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { CreditCard, Check, ArrowLeft, Loader2 } from 'lucide-react';
import Navbar from './shared/Navbar.jsx';
import { sharedStyles, createGlassCard, createButton } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

const PurchaseCredits = () => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const { makeRequest } = useApi();
  const [loading, setLoading] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);

  useEffect(() => {
    const fetchCreditBalance = async () => {
      if (!isLoaded || !isSignedIn) return;

      try {
        const balance = await makeRequest('credits/balance');
        setCreditBalance(balance);
      } catch (error) {
        console.error('Error fetching credit balance:', error);
      }
    };

    fetchCreditBalance();
  }, [isLoaded, isSignedIn, makeRequest]);

  const createCheckoutSession = async (priceId, packName) => {
    setLoading(packName);

    try {
      const { url } = await makeRequest('billing/purchase-credits', {
        method: 'POST',
        body: JSON.stringify({
          price_id: priceId,
          success_url: `${window.location.origin}/dashboard`,
          cancel_url: `${window.location.origin}/purchase-credits`,
        }),
      });

      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(`Failed to start checkout: ${error.message}`);
      setLoading(null);
    }
  };

  const handlePurchase = async (priceId, packName) => {
    if (!isSignedIn) {
      navigate('/sign-up');
      return;
    }

    await createCheckoutSession(priceId, packName);
  };

  const creditPacks = [
    {
      name: '5 Reports',
      subtitle: '25 AI-vetted guests',
      price: '$15',
      pricePerCredit: 'Save 12.5 hours of research',
      savings: null,
      features: [
        '25 perfect guests discovered',
        'Relevance & attainability scoring',
        'Detailed guest summaries',
        'Expertise area mapping',
        'Save $600+ vs manual research',
      ],
      notIncluded: [],
      priceId: import.meta.env.VITE_STRIPE_PRICE_5_CREDITS,
      popular: false,
    },
    {
      name: '10 Reports',
      subtitle: '50 AI-vetted guests',
      price: '$25',
      pricePerCredit: 'Save 25 hours of research',
      savings: '17% savings',
      features: [
        '50 perfect guests discovered',
        'Relevance & attainability scoring',
        'Detailed guest summaries',
        'Expertise area mapping',
        'Save $1,200+ vs manual research',
      ],
      notIncluded: [],
      priceId: import.meta.env.VITE_STRIPE_PRICE_10_CREDITS,
      popular: true,
      badgeText: 'BEST VALUE',
    },
    {
      name: '25 Reports',
      subtitle: '125 AI-vetted guests',
      price: '$50',
      pricePerCredit: 'Save 62.5 hours of research',
      savings: '33% savings',
      features: [
        '125 perfect guests discovered',
        'Relevance & attainability scoring',
        'Detailed guest summaries',
        'Expertise area mapping',
        'Save $3,000+ vs manual research',
      ],
      notIncluded: [],
      priceId: import.meta.env.VITE_STRIPE_PRICE_25_CREDITS,
      popular: false,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
      <Navbar />

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem 1rem',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              ...createButton('secondary', {
                padding: '0.5rem 1rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }),
            }}
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>

          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem',
          }}>
            Purchase Credits
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: sharedStyles.colors.textSecondary,
          }}>
            Credits never expire. Choose the pack that fits your needs.
          </p>

          {/* Current Balance */}
          {creditBalance && (
            <div style={{
              ...createGlassCard('default'),
              padding: '1rem 1.5rem',
              marginTop: '1.5rem',
              display: 'inline-block',
            }}>
              <p style={{
                fontSize: '0.875rem',
                color: sharedStyles.colors.textSecondary,
                marginBottom: '0.25rem',
              }}>
                Current Balance
              </p>
              <p style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: sharedStyles.colors.textPrimary,
              }}>
                {creditBalance.is_byok ? '∞ (BYOK Enabled)' : `${creditBalance.total} credits`}
              </p>
              {!creditBalance.is_byok && (
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '0.5rem',
                  fontSize: '0.75rem',
                  color: sharedStyles.colors.textSecondary,
                }}>
                  {creditBalance.trial > 0 && <span>Trial: {creditBalance.trial}</span>}
                  {creditBalance.basic > 0 && <span>Basic: {creditBalance.basic}</span>}
                  {creditBalance.pro > 0 && <span>Pro: {creditBalance.pro}</span>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Credit Packs Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
        }}>
          {creditPacks.map((pack) => (
            <div
              key={pack.name}
              style={{
                ...createGlassCard('default', {
                  padding: '1.5rem',
                  position: 'relative',
                  transform: pack.popular ? 'scale(1.03)' : 'scale(1)',
                  transition: sharedStyles.transitions.smooth,
                }),
              }}
            >
              {/* Badge */}
              {pack.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '15px',
                  background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '16px',
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.3)',
                }}>
                  {pack.badgeText}
                </div>
              )}

              {/* Pack Name */}
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: sharedStyles.colors.textPrimary,
                marginBottom: '0.25rem',
              }}>
                {pack.name}
              </h3>

              {/* Subtitle */}
              <p style={{
                fontSize: '0.875rem',
                color: sharedStyles.colors.textSecondary,
                marginBottom: '0.75rem',
              }}>
                {pack.subtitle}
              </p>

              {/* Price */}
              <div style={{ marginBottom: '0.5rem' }}>
                <span style={{
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {pack.price}
                </span>
              </div>

              {/* Time Savings */}
              <p style={{
                fontSize: '0.75rem',
                color: sharedStyles.colors.primary,
                fontWeight: '600',
                marginBottom: '0.75rem',
              }}>
                {pack.pricePerCredit}
              </p>

              {/* Savings */}
              {pack.savings && (
                <div style={{
                  background: sharedStyles.colors.primaryLight,
                  color: sharedStyles.colors.primary,
                  padding: '0.375rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  textAlign: 'center',
                  marginBottom: '1rem',
                }}>
                  {pack.savings}
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={() => handlePurchase(pack.priceId, pack.name)}
                disabled={loading !== null}
                style={{
                  ...createButton('primary', {
                    width: '100%',
                    justifyContent: 'center',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    marginBottom: '1rem',
                    opacity: loading && loading !== pack.name ? 0.5 : 1,
                  }),
                }}
              >
                {loading === pack.name ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Loading...
                  </div>
                ) : (
                  <>
                    <CreditCard size={16} style={{ marginRight: '0.5rem' }} />
                    Purchase
                  </>
                )}
              </button>

              {/* Features List */}
              <div style={{ borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '1rem' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {pack.features.map((feature, index) => (
                    <li
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        fontSize: '0.8rem',
                        color: sharedStyles.colors.textPrimary,
                      }}
                    >
                      <Check size={16} style={{ color: sharedStyles.colors.success, flexShrink: 0, marginTop: '0.125rem' }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {pack.notIncluded.map((feature, index) => (
                    <li
                      key={`not-${index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        fontSize: '0.8rem',
                        color: sharedStyles.colors.textMuted,
                        textDecoration: 'line-through',
                      }}
                    >
                      <span style={{ fontSize: '1rem', lineHeight: '1.2' }}>✗</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Subscription Option */}
        <div style={{
          ...createGlassCard('default'),
          padding: '2rem',
          marginTop: '3rem',
          textAlign: 'center',
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: sharedStyles.colors.textPrimary,
            marginBottom: '0.5rem',
          }}>
            Need More Credits Per Month?
          </h2>
          <p style={{
            fontSize: '1rem',
            color: sharedStyles.colors.textSecondary,
            marginBottom: '1.5rem',
          }}>
            Subscriptions include monthly credits that roll over, better value per credit, and automatic renewals.
          </p>
          <button
            onClick={() => navigate('/#pricing')}
            style={{
              ...createButton('secondary', {
                padding: '0.75rem 1.5rem',
              }),
            }}
          >
            View Subscription Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCredits;
